// src/modules/feedback.js — Feedback modal, screenshots, submission, ticket list
import { state } from '../state.js';
import { getSessionToken, apiFetch } from '../utils/api.js';
import { escapeHtml, showView, showConfirmDialog, showToast } from '../utils/dom.js';
import { APP_VERSION, API_BASE, SESSION_ID, PAGE_LOAD_TIME } from '../utils/constants.js';
import {
    logger, sanitizeLogs,
    getRecentNetworkLogs, getRecentUploadProgressLogs, getRecentUploadDiagnostics,
} from './debug.js';

let _feedbackScreenshots = []; // Array of base64 data URLs (max 3)

export function openFeedbackModal() {
    _feedbackScreenshots = [];
    const modal = document.getElementById('feedbackModal');
    if (!modal) return;
    modal.classList.add('active');
    // Reset form
    document.getElementById('feedbackDescription').value = '';
    document.getElementById('feedbackCharCount').textContent = '0 / 5000';
    document.getElementById('feedbackStatus').textContent = '';
    document.getElementById('feedbackStatus').className = 'feedback-status';
    document.getElementById('feedbackThumbnails').innerHTML = '';
    updateFeedbackScreenshotCount();
    // Set default category
    const bugRadio = document.querySelector('input[name="feedbackCategory"][value="bug"]');
    if (bugRadio) bugRadio.checked = true;
    // Populate tech details
    populateFeedbackDiagnostics();
    // Character count listener
    const desc = document.getElementById('feedbackDescription');
    desc.oninput = () => {
        document.getElementById('feedbackCharCount').textContent = desc.value.length + ' / 5000';
    };
    logger.info('SYSTEM', 'Feedback modal opened');
}

export function closeFeedbackModal() {
    const modal = document.getElementById('feedbackModal');
    if (modal) modal.classList.remove('active');
}

export function populateFeedbackDiagnostics() {
    const container = document.getElementById('feedbackTechContent');
    if (!container) return;
    const activePage = document.querySelector('#view-app .page.active');
    const profileName = document.getElementById('profileBadgeName');
    const diagnostics = {
        appVersion: APP_VERSION,
        sessionId: SESSION_ID,
        timestamp: new Date().toISOString(),
        currentTab: activePage ? activePage.id.replace('page-', '') : 'unknown',
        profileName: profileName ? profileName.textContent : 'none',
        screenResolution: window.screen.width + 'x' + window.screen.height,
        viewportSize: window.innerWidth + 'x' + window.innerHeight,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        online: navigator.onLine,
        connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        language: navigator.language,
    };
    // Add recent error logs (last 10)
    const recentErrors = logger.getLogs('ERRORS').slice(-10).map(e => ({
        time: e.timestamp,
        msg: e.message,
        cat: e.category,
    }));
    container.textContent = JSON.stringify({ ...diagnostics, recentErrors }, null, 2);
}

export function collectFeedbackDiagnostics() {
    const activePage = document.querySelector('#view-app .page.active');
    const profileName = document.getElementById('profileBadgeName');
    return {
        appVersion: APP_VERSION,
        sessionId: SESSION_ID,
        timestamp: new Date().toISOString(),
        currentTab: activePage ? activePage.id.replace('page-', '') : 'unknown',
        profileName: profileName ? profileName.textContent : 'none',
        screenResolution: window.screen.width + 'x' + window.screen.height,
        viewportSize: window.innerWidth + 'x' + window.innerHeight,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        online: navigator.onLine,
        connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        language: navigator.language,
        recentErrors: logger.getLogs('ERRORS').slice(-10).map(e => ({
            time: e.timestamp,
            msg: e.message,
            cat: e.category,
        })),
        // Enhanced diagnostics for debugging upload/recording issues
        recentWarnings: logger.getLogs('ALL').filter(l => l.level === 'WARN').slice(-10).map(e => ({
            time: e.timestamp, msg: e.message, cat: e.category,
        })),
        // FIX N1 (defence in depth): these carry entry.data, so run them through
        // sanitizeLogs() before they leave the device. The primary fix is that no
        // raw request/response body is captured in the first place.
        recentUploadLogs: sanitizeLogs(logger.getLogs('ALL').filter(l => l.category === 'UPLOAD').slice(-20)).map(e => ({
            time: e.timestamp, level: e.level, msg: e.message, data: e.data,
        })),
        recentRecordingLogs: sanitizeLogs(logger.getLogs('ALL').filter(l => l.category === 'RECORDING').slice(-15)).map(e => ({
            time: e.timestamp, level: e.level, msg: e.message, data: e.data,
        })),
        recentAuthLogs: logger.getLogs('ALL').filter(l => l.category === 'AUTH').slice(-5).map(e => ({
            time: e.timestamp, level: e.level, msg: e.message,
        })),
        recentNetworkLogs: sanitizeLogs(logger.getLogs('ALL').filter(l => l.category === 'NETWORK' || l.category === 'LIFECYCLE').slice(-10)).map(e => ({
            time: e.timestamp, level: e.level, msg: e.message, data: e.data,
        })),
        // FIX N1: API call metadata only (method/path/status/duration/lengths).
        // Bodies are no longer captured; the sanitizer is a second layer.
        recentApiLogs: sanitizeLogs(getRecentNetworkLogs().slice(-20).map(e => ({ data: e }))).map(e => e.data),
        // v2.9.9: Upload progress/heartbeat logs
        // FIX N7d (E21): these two buffers were the only ones attached to the
        // feedback payload WITHOUT passing through sanitizeLogs. Both carry
        // errorMessage strings taken from failed S3 PUTs against a presigned URL,
        // and a presigned URL's path is the audio key. Wrapped in the same
        // {data:…} shape recentApiLogs already uses so the scrubber applies.
        // Revert note: to roll back FIX N7d, restore
        //   uploadProgressLogs: recentUploadProgressLogs.slice(-30),
        //   recentUploadDiagnostics: recentUploadDiagnostics.slice(-5),
        uploadProgressLogs: sanitizeLogs(getRecentUploadProgressLogs().slice(-30).map(e => ({ data: e }))).map(e => e.data),
        // v2.9.11: Comprehensive upload diagnostics (pre-upload + result telemetry)
        recentUploadDiagnostics: sanitizeLogs(getRecentUploadDiagnostics().slice(-5).map(e => ({ data: e }))).map(e => e.data),
        sessionUptime: Math.round((Date.now() - PAGE_LOAD_TIME) / 1000),
        totalLogEntries: logger.getLogs('ALL').length,
        pendingUploadExists: !!state.pendingUploadBlob,
        pendingUploadSize: state.pendingUploadBlob ? state.pendingUploadBlob.size : 0,
        currentSessionDbId: state.currentSessionDbId,
        isRecording: state.isRecording,
        isPaused: state.isPaused,
        recordingMode: state.recordingMode,
        recordingSegments: state.recordingSegments.length,
        recordingSegmentsTotalSize: state.recordingSegments.reduce((s, b) => s + b.size, 0),
        memoryUsage: navigator.deviceMemory ? navigator.deviceMemory + ' GB' : 'unknown',
        connectionDetails: navigator.connection ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData,
        } : null,
        // Log point 5: Include jobId for CloudWatch correlation
        lastProcessingJobId: state.lastProcessingJobId || null,
    };
}

export function updateFeedbackScreenshotCount() {
    const countEl = document.getElementById('feedbackScreenshotCount');
    if (countEl) countEl.textContent = '(' + _feedbackScreenshots.length + '/3)';
    // Disable buttons if at max
    const captureBtn = document.getElementById('btnFeedbackCapture');
    const uploadBtns = document.querySelectorAll('.btn-feedback-screenshot');
    if (_feedbackScreenshots.length >= 3) {
        uploadBtns.forEach(btn => btn.disabled = true);
    } else {
        uploadBtns.forEach(btn => btn.disabled = false);
    }
}

export function renderFeedbackThumbnails() {
    const container = document.getElementById('feedbackThumbnails');
    if (!container) return;
    container.innerHTML = _feedbackScreenshots.map((dataUrl, i) => {
        return '<div class="feedback-thumbnail">' +
            '<img src="' + dataUrl + '" alt="Screenshot ' + (i+1) + '">' +
            '<button class="feedback-thumbnail-remove" data-action="remove-feedback-screenshot" data-index="' + i + '">&times;</button>' +
            '</div>';
    }).join('');
    updateFeedbackScreenshotCount();
}

export function removeFeedbackScreenshot(index) {
    _feedbackScreenshots.splice(index, 1);
    renderFeedbackThumbnails();
}

export async function captureFeedbackScreenshot() {
    if (_feedbackScreenshots.length >= 3) return;
    
    const modalOverlay = document.getElementById('feedbackModal')?.closest('.modal-overlay') || document.getElementById('feedbackModal')?.parentElement;
    
    try {
        // Hide feedback modal and debug elements so they're not captured
        if (modalOverlay) modalOverlay.style.display = 'none';
        const debugPanel = document.getElementById('debugPanel');
        const debugBtn = document.getElementById('debugToggleBtn');
        if (debugPanel) debugPanel.style.display = 'none';
        if (debugBtn) debugBtn.style.display = 'none';
        
        await new Promise(r => setTimeout(r, 200));

        // Use html2canvas to capture the visible page
        const canvas = await html2canvas(document.body, {
            width: window.innerWidth,
            height: window.innerHeight,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            x: window.scrollX,
            y: window.scrollY,
            scale: 1,
            useCORS: true,
            allowTaint: true,
            logging: false,
        });
        
        // Resize to max 800px width
        const resizedCanvas = document.createElement('canvas');
        const scale = Math.min(1, 800 / canvas.width);
        resizedCanvas.width = canvas.width * scale;
        resizedCanvas.height = canvas.height * scale;
        const ctx = resizedCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
        
        const dataUrl = resizedCanvas.toDataURL('image/png', 0.8);
        _feedbackScreenshots.push(dataUrl);
        
        // Restore hidden elements
        if (modalOverlay) modalOverlay.style.display = '';
        if (debugPanel) debugPanel.style.display = '';
        if (debugBtn) debugBtn.style.display = '';
        
        renderFeedbackThumbnails();
        logger.info('SYSTEM', 'App screenshot captured for feedback');
    } catch (err) {
        // Restore on failure
        if (modalOverlay) modalOverlay.style.display = '';
        const debugPanel = document.getElementById('debugPanel');
        const debugBtn = document.getElementById('debugToggleBtn');
        if (debugPanel) debugPanel.style.display = '';
        if (debugBtn) debugBtn.style.display = '';
        
        logger.error('SYSTEM', 'Screenshot capture failed', err);
        const statusEl = document.getElementById('feedbackStatus');
        if (statusEl) {
            statusEl.textContent = 'Screenshot capture failed. Please use Upload Image instead.';
            statusEl.className = 'feedback-status error';
        }
    }
}

export function handleFeedbackFileSelect(event) {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const remaining = 3 - _feedbackScreenshots.length;
    const toProcess = files.slice(0, remaining);

    toProcess.forEach(file => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            // Resize image to max 800px width
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const scale = Math.min(1, 800 / img.width);
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/png', 0.8);
                if (_feedbackScreenshots.length < 3) {
                    _feedbackScreenshots.push(dataUrl);
                    renderFeedbackThumbnails();
                }
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });

    // Reset file input
    event.target.value = '';
}

export async function submitFeedback() {
    const description = document.getElementById('feedbackDescription').value.trim();
    const categoryEl = document.querySelector('input[name="feedbackCategory"]:checked');
    const category = categoryEl ? categoryEl.value : 'bug';
    const statusEl = document.getElementById('feedbackStatus');
    const submitBtn = document.getElementById('btnFeedbackSubmit');

    // Validate
    if (!description || description.length < 10) {
        statusEl.textContent = 'Please enter at least 10 characters in the description.';
        statusEl.className = 'feedback-status error';
        return;
    }

    // Disable submit
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';
    statusEl.textContent = '';
    statusEl.className = 'feedback-status';

    try {
        if (!getSessionToken()) { // FIX B12: single accessor
            statusEl.textContent = 'Not authenticated. Please sign in again.';
            statusEl.className = 'feedback-status error';
            return;
        }

        const payload = {
            category: category,
            description: description,
            screenshots: _feedbackScreenshots, // base64 data URLs
            diagnostics: collectFeedbackDiagnostics(),
            // FIX N1: the feedback path never ran the scrubber. It does now.
            sessionLogs: sanitizeLogs(logger.getLogs('ALL')),
        };

        const resp = await apiFetch('/feedback/submit', {
            method: 'POST',
            body: JSON.stringify(payload),
        });

        const data = await resp.json();

        if (resp.ok) {
            statusEl.textContent = 'Feedback submitted successfully! Ticket ID: ' + data.ticketId.substring(0, 8) + '...';
            statusEl.className = 'feedback-status success';
            logger.info('SYSTEM', 'Feedback submitted', { ticketId: data.ticketId, category });
            // Reset form after short delay
            setTimeout(() => {
                closeFeedbackModal();
            }, 2000);
        } else {
            statusEl.textContent = data.error || 'Failed to submit feedback.';
            statusEl.className = 'feedback-status error';
            logger.error('SYSTEM', 'Feedback submission failed', { status: resp.status, error: data.error });
        }
    } catch (err) {
        statusEl.textContent = 'Network error. Please check your connection.';
        statusEl.className = 'feedback-status error';
        logger.error('SYSTEM', 'Feedback submission network error', { error: err.message });
    } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit Feedback';
    }
}

// --- Ticket List ---

export async function openTicketList() {
    const modal = document.getElementById('ticketListModal');
    if (!modal) return;
    modal.classList.add('active');
    const listEl = document.getElementById('ticketList');
    listEl.innerHTML = '<div class="empty-state"><p>Loading tickets...</p></div>';

    try {
        if (!getSessionToken()) { // FIX B12: single accessor
            listEl.innerHTML = '<div class="empty-state"><p>Not authenticated.</p></div>';
            return;
        }

        const resp = await apiFetch('/feedback/list', {
            method: 'GET',
        });

        const data = await resp.json();

        if (!resp.ok) {
            // FIX N5: server-supplied error text is escaped before it hits innerHTML
            listEl.innerHTML = '<div class="empty-state"><p>' + escapeHtml(data.error || 'Failed to load tickets.') + '</p></div>';
            return;
        }

        if (!data.tickets || data.tickets.length === 0) {
            listEl.innerHTML = '<div class="empty-state"><p>No tickets submitted yet.</p></div>';
            return;
        }

        listEl.innerHTML = data.tickets.map(ticket => {
            const date = ticket.submittedAt ? new Date(ticket.submittedAt).toLocaleDateString() : '';
            const categoryIcons = { bug: '🐛', feedback: '💡', crash: '💥', suggestion: '💡' };
            const icon = categoryIcons[ticket.category] || '📝';
            const statusColors = { submitted: '#95a5a6', acknowledged: '#f39c12', 'in-progress': '#3498db', resolved: '#27ae60' };
            const statusColor = statusColors[ticket.status] || '#95a5a6';
            return '<div class="ticket-card" data-action="show-ticket-detail" data-id="' + ticket.ticketId + '">' +
                '<div class="ticket-card-header">' +
                    '<span class="ticket-card-category ' + ticket.category + '">' + icon + ' ' + ticket.category + '</span>' +
                    '<span class="ticket-card-status ' + ticket.status + '" style="background:' + statusColor + ';color:#fff;padding:2px 8px;border-radius:10px;font-size:11px">' + ticket.status + '</span>' +
                '</div>' +
                '<div class="ticket-card-description">' + escapeHtml(ticket.description) + '</div>' +
                (ticket.status === 'resolved' && ticket.resolvedInVersion ? '<div class="ticket-resolved-info">✅ Fixed in v' + escapeHtml(ticket.resolvedInVersion) + '</div>' : '') +
                (ticket.adminNote ? '<div class="ticket-admin-response"><strong>Response from team:</strong> ' + escapeHtml(ticket.adminNote) + '</div>' : '') +
                '<div class="ticket-card-meta">' +
                    '<span>📅 ' + date + '</span>' +
                    (ticket.screenshotCount > 0 ? '<span>📎 ' + ticket.screenshotCount + ' screenshot' + (ticket.screenshotCount > 1 ? 's' : '') + '</span>' : '') +
                '</div>' +
            '</div>';
        }).join('');

    } catch (err) {
        listEl.innerHTML = '<div class="empty-state"><p>Network error loading tickets.</p></div>';
        logger.error('SYSTEM', 'Failed to load ticket list', { error: err.message });
    }
}

export function closeTicketList() {
    const modal = document.getElementById('ticketListModal');
    if (modal) modal.classList.remove('active');
}

export function showTicketDetail(ticketId) {
    // Close ticket list and show detail
    closeTicketList();
    const modal = document.getElementById('ticketDetailModal');
    if (!modal) return;
    modal.classList.add('active');

    const content = document.getElementById('ticketDetailContent');
    content.innerHTML = '<div class="empty-state"><p>Loading ticket details...</p></div>';

    loadTicketDetailContent(ticketId, content);
}

export function loadTicketDetailContent(ticketId, content) {
    // FIX B12: was reading the never-written 'dentscribe_session_token' key and
    // sending 'Bearer null'. Routed through the single accessor.
    const token = getSessionToken();
    if (!token) {
        content.innerHTML = '<div class="empty-state"><p>Not authenticated. Please sign in again.</p></div>';
        return;
    }
    fetch(API_BASE + '/feedback/list', {
        method: 'GET',
        headers: { 'Authorization': 'Bearer ' + token },
    })
    .then(r => r.json())
    .then(data => {
        const ticket = (data.tickets || []).find(t => t.ticketId === ticketId);
        if (!ticket) {
            content.innerHTML = '<div class="empty-state"><p>Ticket not found.</p></div>';
            return;
        }
        const date = ticket.submittedAt ? new Date(ticket.submittedAt).toLocaleString() : 'Unknown';
        const categoryIcons = { bug: '🐛 Bug Report', feedback: '💡 Suggestion', crash: '💥 Crash/Error', suggestion: '💡 Suggestion' };
        const statusColors = { submitted: '#95a5a6', acknowledged: '#f39c12', 'in-progress': '#3498db', resolved: '#27ae60' };
        const statusColor = statusColors[ticket.status] || '#95a5a6';

        const isResolved = ticket.status === 'resolved';
        const resolveBtn = isResolved ? '' :
            '<button class="btn btn-primary" style="margin-top:12px;margin-right:8px" data-action="resolve-ticket-by-user" data-id="' + ticketId + '">✅ Mark as Resolved</button>';
        const refreshBtn = '<button class="btn btn-secondary" style="margin-top:12px" data-action="refresh-ticket-detail" data-id="' + ticketId + '">🔄 Refresh</button>';

        content.innerHTML =
            '<div class="ticket-detail-field"><label>Category</label><p>' + (categoryIcons[ticket.category] || ticket.category) + '</p></div>' +
            '<div class="ticket-detail-field"><label>Status</label><p><span class="ticket-card-status ' + ticket.status + '" style="background:' + statusColor + ';color:#fff;padding:2px 10px;border-radius:10px;font-size:12px">' + ticket.status + '</span></p></div>' +
            '<div class="ticket-detail-field"><label>Submitted</label><p>' + date + '</p></div>' +
            '<div class="ticket-detail-field"><label>Description</label><p>' + escapeHtml(ticket.description) + '</p></div>' +
            (ticket.screenshotCount > 0 ? '<div class="ticket-detail-field"><label>Screenshots</label><p>' + ticket.screenshotCount + ' attached</p></div>' : '') +
            (ticket.adminNote ? '<div class="ticket-detail-field ticket-admin-response"><label>Response from team</label><p>' + escapeHtml(ticket.adminNote) + '</p></div>' : '') +
            (isResolved && ticket.resolvedInVersion ? '<div class="ticket-detail-field ticket-resolved-info"><label>Resolution</label><p>✅ Fixed in v' + escapeHtml(ticket.resolvedInVersion) + '</p></div>' : '') +
            '<div class="ticket-detail-actions" style="margin-top:16px;border-top:1px solid var(--border-color);padding-top:12px">' + resolveBtn + refreshBtn + '</div>';
    })
    .catch(err => {
        content.innerHTML = '<div class="empty-state"><p>Failed to load ticket details.</p></div>';
    });
}

export async function resolveTicketByUser(ticketId) {
    var confirmed = await showConfirmDialog('Resolve Ticket', 'Mark this ticket as resolved?', 'Continue', 'btn-primary');
    if (!confirmed) return;
    try {
        // FIX B12: was 'Bearer null' — routed through the single accessor.
        const token = getSessionToken();
        if (!token) { showToast('Not authenticated. Please sign in again.', 'error'); return; }
        const res = await fetch(API_BASE + '/feedback/' + ticketId + '/user-resolve', {
            method: 'PATCH',
            headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
            const err = await res.json();
            showToast('Failed to resolve: ' + (err.error || 'Unknown error'), 'error');
            return;
        }
        // Refresh the detail view
        refreshTicketDetail(ticketId);
    } catch (err) {
        showToast('Error resolving ticket: ' + err.message, 'error');
    }
}

export function refreshTicketDetail(ticketId) {
    const content = document.getElementById('ticketDetailContent');
    if (content) {
        content.innerHTML = '<div class="empty-state"><p>Refreshing...</p></div>';
        loadTicketDetailContent(ticketId, content);
    }
}

export function closeTicketDetail() {
    const modal = document.getElementById('ticketDetailModal');
    if (modal) modal.classList.remove('active');
}



// No-op: callers still reference this after live-transcribe removal
export function hideLiveTranscriptUI() {}

export function showAudioFallbackButton() {
    // Remove any existing fallback button
    const existing = document.querySelector('.btn-fallback-upload');
    if (existing) existing.remove();
    // Show a button that lets user fall back to uploading the audio
    const fallbackHtml = '<button class="btn-fallback-upload" data-action="fallback-to-audio-upload">Upload Audio Instead</button>';
    const statusEl = document.getElementById('recordStatus');
    if (statusEl) statusEl.insertAdjacentHTML('afterend', fallbackHtml);
}

export async function fallbackToAudioUpload() {
    // Remove fallback button
    const btn = document.querySelector('.btn-fallback-upload');
    if (btn) btn.remove();
    hideLiveTranscriptUI();
    // Use the existing audio upload flow with the recorded segments
    const finalBlob = await combineSegments();
    if (finalBlob && finalBlob.size > 0) {
        await uploadAndProcess(finalBlob, 'record', state.lastSegmentJoinInfo); // FIX A3
    } else {
        setStatus('recordStatus', 'No audio recorded to upload.', 'error');
        showNewRecordingBtn('record');
    }
}

